import * as React from 'react';
import type {ChatMessage, ActionEvent} from 'src/components/agentic-ui';
import type {ApiPostOptions} from 'src/utils/redux-api';
import {useDispatch, useSelector} from 'react-redux';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import {
  ChatPanelLexical,
  type ChatPanelHandle,
} from 'src/components/agentic-ui/chat-panel';
import {
  selectFullscreen,
  selectSideNavExpanded,
} from 'src/selectors/app-config';
import {
  useRespondToAutoAttach,
  useRequestToAutoAttach,
  useHeaderSlot,
} from 'src/components/agentic-ui/use-auto-attach';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {classify} from '@spaced-out/ui-design-system/lib/utils/classify';
import {useApiMutation} from 'src/hooks/useApiMutation';

import {AgenticSuggestions} from 'src/components/agentic-ui/agentic-suggestions';
import {useApiQuery} from 'src/hooks/useApiQuery';
import * as reduxApiAskAi from 'src/utils/redux-api-ask-ai';
import css from './ask-ai.css';
import {skipToken} from '@tanstack/react-query';
import {
  registerChatPanel,
  useAskAIProduct,
  useAskAIThreadId,
  useShouldFetchStarterPrompts,
  setThreadId as setThreadIdInStore,
  clearThreadId as clearThreadIdInStore,
  initNewThread,
} from './ask-ai-store';
import {runPreSendCollectors} from 'src/components/agentic-ui/context-collectors';
import {publishAgenticEvent} from 'src/components/agentic-ui/agentic-ui-event-bus';
import {useHistory} from 'src/rerouter';
import {useAgencyApiHeaders} from 'src/components/agentic-ui';


const LAST_MODE_STORAGE_KEY = 'askai:chatMode';
const POSITION_PERSIST_KEY = 'askai:chatPos';
const PANEL_LEFT_MARGIN = 8;
const SIDENAV_EXPANDED_WIDTH = 240;
const SIDENAV_COLLAPSED_WIDTH = 60;

type ThreadCreatePayload = {
  name: string;
  context: {
    page_context: {
      product: string;
    };
  };
};

export const AskAI = () => {
  const {t} = useLabelTranslation();
  const {
    isExtension,
    isClientIframe,
    isSenseAiIframe,
    isTrmIframe,
    hasContextInUrl,
  } = useEnvironmentContext();
  const chatPanelRef = React.useRef<ChatPanelHandle | null>(null);
  const dispatch = useDispatch();
  const {headerSlot} = useRespondToAutoAttach(chatPanelRef);
  const product = useAskAIProduct();
  const threadId = useAskAIThreadId();
  const shouldFetchStarterPrompts = useShouldFetchStarterPrompts();
  const history = useHistory();
  const agencyHeaders = useAgencyApiHeaders();
  const isFullscreenExperience = useSelector(selectFullscreen);
  const isSideNavExpanded = useSelector(selectSideNavExpanded);

  const panelLeftMargin = React.useMemo(() => {
    if (isFullscreenExperience) {
      return PANEL_LEFT_MARGIN;
    }

    return (
      (isSideNavExpanded ? SIDENAV_EXPANDED_WIDTH : SIDENAV_COLLAPSED_WIDTH) +
      PANEL_LEFT_MARGIN
    );
  }, [isFullscreenExperience, isSideNavExpanded]);

  const {data: threadData} = useApiQuery<{messages: ChatMessage[]}>(
    `conversation/${threadId}`,
    {
      queryOptions: {
        queryKey: threadId ? ['message-history', threadId] : skipToken,
        enabled: !!threadId,
      },
      apiOptions: {
        method: 'GET',
        options: {
          apiPath: '/grace/api/v1/',
          headers: agencyHeaders,
        },
      },
    },
  );
  const messageHistory = threadData?.messages || [];

  //if threadId is not there make an api call using useApiMutation to create a thread and set it in local storage
  const {mutateAsync: createThread, isPending: _isUpserting} = useApiMutation({
    mutationFn: async (payload: ThreadCreatePayload) => {
      return dispatch(
        reduxApiAskAi.post('conversation', payload, undefined, {
          headers: agencyHeaders,
        } as ApiPostOptions),
      );
    },
  });

  const handleCreateThread = async (): Promise<string> => {
    const thread = await createThread({
      name: 'Dummy thread name',
      context: {
        page_context: {
          product,
        },
      },
    });
    setThreadIdInStore(thread.id);
    return thread.id;
  };

  const handleClearThread = () => {
    // TODO:(diwakersurya)for now, default to workflow to get proper starter prompts
    // FIXME:(diwakersurya)it should remember the product from askai store.
    initNewThread({source: 'workflow', shouldFetchStarterPrompts: true});
    //initNewThread({source: product, shouldFetchStarterPrompts: true});
  };

  const getStreamUrl = async () => {
    const id = threadId ?? (await handleCreateThread());

    return `/grace/api/v1/conversation/${id}/chat`;
  };

  //NOTE:(diwakersurya) Do not show AskAI in extension, client iframe, or sense ai iframe.
  // hasContextInUrl is final if the url contains context, that means we are loading in a
  // separate iframe/environment other than app. we don't want to open sense ai there.
  if (
    isExtension ||
    isClientIframe ||
    isSenseAiIframe ||
    isTrmIframe ||
    hasContextInUrl
  ) {
    return null;
  }
  return (
    <>
      <ChatPanelLexical
        ref={(node) => {
          chatPanelRef.current = node;
          registerChatPanel(node);
        }}
        attachChatTo="#chat-container"
        attachHeaderTo="#chat-header-container"
        defaultMode="floating"
        placeholder={t('Ask_me_anything', 'Ask me anything')}
        lastModePersistKey={LAST_MODE_STORAGE_KEY}
        positionPersistKey={POSITION_PERSIST_KEY}
        renderStartScreen={({publishMessageAction}) => {
          if (shouldFetchStarterPrompts) {
            return (
              <AgenticSuggestions
                publishMessageAction={
                  publishMessageAction as (action: ActionEvent) => void
                }
              />
            );
          }
          return null;
        }}
        messageHistory={messageHistory}
        //we need this prop in attached mode to allow host to provide
        //custom content in the header slot
        headerSlot={headerSlot}
        height={400}
        chatOptions={{
          // resolve SSE URL lazily (creates thread on first send if needed)
          getStreamUrl,
          headers: {
            Authorization: `Bearer ${'test'}`,
            ...agencyHeaders,
          },
          collectMessageContext: () =>
            runPreSendCollectors({history}, undefined),
          publishMessageAction: (action: ActionEvent) => {
            try {
              const {product, event, payload, dedupeKey} = (action || {}) as {
                product: unknown;
                event: unknown;
                payload?: unknown;
                dedupeKey?: string;
              };
              if (event) {
                const emit = publishAgenticEvent as unknown as (
                  product: unknown,
                  event: unknown,
                  payload: unknown,
                  dedupeKey?: string,
                ) => void;
                emit(product, event, payload ?? null, dedupeKey);
              }
            } catch {}
          },
          backoff: {
            baseMs: 1000,
            maxMs: 15000,
            maxAttempts: 1,
          },
        }}
        defaultOpen={false}
        onReset={handleClearThread}
        panelLeftMargin={panelLeftMargin}
        onClose={handleClearThread}
      />
    </>
  );
};

export interface AskAIChatHostProps {
  className?: string;
}

export const AskAIChatHost = ({className}: AskAIChatHostProps) => {
  useRequestToAutoAttach();
  return (
    <div
      id="chat-container"
      className={classify(css.panelWrapper, className)}
    ></div>
  );
};
export interface AskAIHeaderHostProps {
  className?: string;
  slot?: React.ReactNode;
}

export const AskAIHeaderHost = ({className, slot}: AskAIHeaderHostProps) => {
  useHeaderSlot(slot);
  return (
    <div
      id="chat-header-container"
      className={classify(css.headerWrapper, className)}
    ></div>
  );
};
